import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { AuthModalComponent, AuthMode } from '../auth-modal/auth-modal.component';
import { SessionService } from '../../services/session.service';
import { NgxTgModule } from '@worthy-ventures/metaglotta-ngx';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-menu-bar',
  imports: [FontAwesomeModule, NgxTgModule],
  templateUrl: './menu-bar.component.html',
  styleUrl: './menu-bar.component.scss'
})
export class MenuBarComponent {
  isMenuOpen = false;
  /** Account dropdown. Desktop opens it on hover; touch needs this toggle. */
  isAccountOpen = false;
  /** Language dropdown — same open/close model as the account menu. */
  isLangOpen = false;
  readonly faChevronDown = faChevronDown;

  constructor(
    private router: Router,
    private modalService: NgbModal,
    public session: SessionService,
    private languageService: LanguageService,
  ) { }

  get languages() {
    return this.languageService.languages;
  }

  /** Bound to the switcher — the active language code, e.g. 'en' | 'el'. */
  get selectedLang(): string {
    return this.languageService.current;
  }

  /** What the switcher shows, e.g. 'EN' | 'GR'. */
  get selectedLangLabel(): string {
    const code = this.selectedLang;
    return this.languages.find((l) => l.code === code)?.label ?? code.toUpperCase();
  }

  public changeLanguage(code: string) {
    this.languageService.use(code);
    // Collapse the list but leave the burger panel open, so the nav labels
    // visibly switch language where the user is already looking.
    this.isLangOpen = false;
  }

  public openAuth(mode: AuthMode) {
    this.closeMenu();
    const ref = this.modalService.open(AuthModalComponent, {
      centered: true,
      windowClass: 'auth-modal-window',
    });
    ref.componentInstance.mode = mode;
  }

  public logout() {
    this.closeMenu();
    this.session.clear();
    this.router.navigate(['/']);
  }

  public goTo(page: string) {
    this.closeMenu();
    this.router.navigate([`/${page}`]);
  }

  public scrollToSection(sectionId: string) {
    this.closeMenu();
    const isHome = this.router.url === '/' || this.router.url.startsWith('/?');
    if (isHome) {
      const el = document.getElementById(sectionId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      this.router.navigate(['/'], { queryParams: { scrollTo: sectionId } });
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    // a collapsed panel should not reopen with a list already down
    if (!this.isMenuOpen) {
      this.isAccountOpen = false;
      this.isLangOpen = false;
    }
  }

  toggleAccount() {
    this.isAccountOpen = !this.isAccountOpen;
    // only one list expanded at a time — stacked, they push the panel long
    this.isLangOpen = false;
  }

  toggleLang() {
    this.isLangOpen = !this.isLangOpen;
    this.isAccountOpen = false;
  }

  /** Collapses the mobile panel (and its sub-lists) after navigating. */
  private closeMenu() {
    this.isMenuOpen = false;
    this.isAccountOpen = false;
    this.isLangOpen = false;
  }
}
